import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconPackage } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { StatusChip, type OrderStatus } from "../../../../components/common/chip";
import { Button } from "../../../../components/common/button";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { Pagination } from "../../../../components/common/pagination";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { Select } from "../../../../components/forms/select";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { SalesQueryDtoSortOrderEnum, type SaleDto } from "../../../../api";
import { useOrders, type OrdersFilter } from "../../hooks/useOrders";
import {
  deriveOrderStatus,
  formatLongDate,
} from "../../utils/order-status";
import { OrdersStats } from "../components/orders-stats";

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "delivered", label: "Delivered" },
  { value: "inTransit", label: "In Transit" },
  { value: "processing", label: "Processing" },
  { value: "cancelled", label: "Canceled" },
] as const satisfies readonly { value: OrdersFilter; label: string }[];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "amount_desc", label: "Amount: High to Low" },
  { value: "amount_asc", label: "Amount: Low to High" },
] as const;

type SortValue = (typeof SORT_OPTIONS)[number]["value"];

const DATE_LABEL: Record<OrderStatus, string> = {
  delivered: "Delivered",
  inTransit: "Expected",
  processing: "Expected",
  validated: "Validated",
  cancelled: "Refunded",
  rejected: "Rejected",
};

const PAGE_SIZE = 10;

const SORT_QUERY: Record<SortValue, { sortBy: string; sortOrder: SalesQueryDtoSortOrderEnum }> = {
  newest: { sortBy: "date_order", sortOrder: SalesQueryDtoSortOrderEnum.Desc },
  oldest: { sortBy: "date_order", sortOrder: SalesQueryDtoSortOrderEnum.Asc },
  amount_desc: { sortBy: "amount_total", sortOrder: SalesQueryDtoSortOrderEnum.Desc },
  amount_asc: { sortBy: "amount_total", sortOrder: SalesQueryDtoSortOrderEnum.Asc },
};

const VALID_FILTERS = new Set<OrdersFilter>(["all", "delivered", "inTransit", "processing", "cancelled"]);
const VALID_SORTS = new Set<SortValue>(["newest", "oldest", "amount_desc", "amount_asc"]);

function parseFilter(v: string | null): OrdersFilter {
  return v && VALID_FILTERS.has(v as OrdersFilter) ? (v as OrdersFilter) : "all";
}
function parseSort(v: string | null): SortValue {
  return v && VALID_SORTS.has(v as SortValue) ? (v as SortValue) : "newest";
}

export function SalesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const filter = parseFilter(params.get("status"));
  const sort = parseSort(params.get("sort"));
  const search = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const setParam = useCallback(
    (updates: Record<string, string | undefined>) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (v === undefined || v === "") next.delete(k);
          else next.set(k, v);
        }
        return next;
      });
    },
    [setParams],
  );

  const isClientFiltered = filter !== "all" && filter !== "cancelled";
  const query = useMemo(
    () => ({
      page: isClientFiltered ? 1 : page,
      pageSize: isClientFiltered ? 50 : PAGE_SIZE,
      search: search.trim() || undefined,
      ...SORT_QUERY[sort],
    }),
    [page, search, sort, isClientFiltered],
  );

  const { data, error, isLoading } = useOrders(filter, query);

  const items = useMemo(() => {
    const list = data?.data ?? [];
    if (filter === "all" || filter === "cancelled") return list;
    if (filter === "delivered") {
      return list.filter((s) => {
        const st = deriveOrderStatus(s);
        return st === "delivered" || st === "validated";
      });
    }
    return list.filter((s) => deriveOrderStatus(s) === filter);
  }, [data, filter]);

  const pageCount = isClientFiltered ? 1 : Math.max(1, data?.meta?.pageCount ?? 1);

  const handleFilter = useCallback((next: OrdersFilter) => {
    setParam({ status: next === "all" ? undefined : next, page: undefined });
  }, [setParam]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setParam({ q: e.target.value || undefined, page: undefined });
  }, [setParam]);

  const handleSort = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setParam({ sort: e.target.value === "newest" ? undefined : e.target.value, page: undefined });
  }, [setParam]);

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6 max-md:gap-4 max-md:px-4">
      <div>
        <Heading variant="h2" tone="dark">Orders</Heading>
        <Text variant="bodyLg" tone="muted">Track and manage your orders</Text>
      </div>

      <OrdersStats />

      <div className="flex items-center gap-8 max-md:flex-wrap max-md:gap-3">
        <SearchBar
          placeholder="Search by order number"
          value={search}
          onChange={handleSearch}
          wrapperClassName="flex-1 max-md:basis-full"
        />

        <SegmentedControl
          options={FILTER_OPTIONS}
          value={filter}
          onChange={handleFilter}
          aria-label="Filter orders by status"
        />

        <Select
          name="orders-sort"
          value={sort}
          onChange={handleSort}
          options={SORT_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          density="dense"
          fullWidth={false}
          className="min-w-[210px]"
        />
      </div>

      <OrdersList
        items={items}
        isLoading={isLoading}
        error={!!error}
        onView={(id) => navigate(`${AppRoutes.ORDERS}/${id}`)}
      />

      {pageCount > 1 && (
        <div className="flex justify-center">
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(p) => setParam({ page: p > 1 ? String(p) : undefined })}
          />
        </div>
      )}
    </div>
  );
}

interface OrdersListProps {
  items: SaleDto[];
  isLoading: boolean;
  error: boolean;
  onView: (id: number) => void;
}

function OrdersList({ items, isLoading, error, onView }: OrdersListProps) {
  if (isLoading) {
    return (
      <ShimmerLoader message="Loading your orders…">
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Shimmer key={i} className="h-[88px] w-full" />
          ))}
        </div>
      </ShimmerLoader>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-8 text-center">
        <Text variant="bodyBold" tone="dark">We couldn't load your orders.</Text>
        <Text variant="bodySm" tone="muted">Please try again in a moment.</Text>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={IconPackage}
        title="No orders found"
        description="Confirmed orders will appear here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((sale) => (
        <OrderCard key={sale.id} sale={sale} onView={() => onView(sale.id)} />
      ))}
    </div>
  );
}

function getActionLabel(status: OrderStatus): string | null {
  switch (status) {
    case "delivered": return "Confirm Receipt";
    case "inTransit": return "Track Order";
    default: return null;
  }
}

function OrderCard({ sale, onView }: { sale: SaleDto; onView: () => void }) {
  const status = deriveOrderStatus(sale);
  const itemCount = sale.orderLineIds?.length ?? 0;
  const currency = sale.currency?.name ?? "QAR";
  const statusDate =
    status === "delivered" || status === "validated"
      ? sale.writeDate
      : sale.validityDate ?? sale.writeDate;

  const actionLabel = getActionLabel(status);

  return (
    <div
      className={`flex items-center rounded-2xl border bg-bgWhite px-6 py-5 cursor-pointer transition-colors max-md:grid max-md:grid-cols-2 max-md:gap-x-4 max-md:gap-y-3 max-md:px-4 max-md:py-4 ${
        status === "delivered"
          ? "border-accentGreen/40 hover:border-accentGreen"
          : "border-borderDefault hover:border-textBody"
      }`}
      onClick={onView}
    >
      <div className="flex-1">
        <Text variant="bodyBold" tone="dark">{sale.name}</Text>
        <Text variant="bodySm" tone="muted" className="mt-1">
          Placed on {formatLongDate(sale.dateOrder)}
        </Text>
      </div>

      <div className="flex-1">
        <StatusChip status={status} />
        <Text variant="bodySm" tone="muted" className="mt-1">Status</Text>
      </div>

      <div className="flex-1">
        <Text variant="bodyBold" tone="dark">
          {itemCount} product{itemCount === 1 ? "" : "s"}
        </Text>
        <Text variant="bodySm" tone="muted" className="mt-1">Items</Text>
      </div>

      <div className="flex-1">
        <CurrencyAmount
          amount={sale.amountTotal}
          currency={currency}
          variant="bodyBold"
          tone="dark"
        />
        <Text variant="bodySm" tone="muted" className="mt-1">Total Amount</Text>
      </div>

      <div className="flex-1">
        <Text variant="bodyBold" tone="dark">{formatLongDate(statusDate)}</Text>
        <Text variant="bodySm" tone="muted" className="mt-1">{DATE_LABEL[status]}</Text>
      </div>

      <Button
        variant={actionLabel ? "primaryGreen" : "secondary"}
        size="sm"
        className="max-md:col-span-2"
        onClick={(e) => { e.stopPropagation(); onView(); }}
      >
        {actionLabel ?? "View"}
      </Button>
    </div>
  );
}
